import { useQuery } from '@tanstack/react-query';
import { CollapseProps, Descriptions, Divider, Flex, Grid, Skeleton, Typography } from 'antd';
import { Fragment, useContext, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getAdditionalWorkingTimeTicket } from 'src/apis/hr_ticket/additionalWorkingTimeTicket.api';
import { getBiometricsDataLog } from 'src/apis/hr_ticket/biometricDataLog.api';
import { BtnHrHandle } from 'src/components/v2/Button';
import CollapseCustom from 'src/components/v2/Collapse';
import { HRTicketStatusTag } from 'src/components/v2/Tag/TicketStatusTag/TicketStatusTag';
import { AppContext } from 'src/contexts/app.context';
import { HrTicketDetailContext } from 'src/contexts/hrTicketDetail.context';
import { TicketContext } from 'src/contexts/ticket.context';
import { IAdditionalWorkingTimeTicketDetail } from 'src/types/hr_ticket/additionalWorkingTimeTicket.type';
import { formatDate, formatTime, formatTimeString } from 'src/utils/utils';

import './AdditionalWorkingTimeTicketDetail.scss';
const { Title, Text } = Typography;

const { useBreakpoint } = Grid;

const BiometricLogs = ({ employeeCode, date }: { employeeCode: string; date: Date }) => {
  const { data, isFetching } = useQuery({
    queryKey: ['biometricLogs', employeeCode, formatDate(date)],
    queryFn: () => getBiometricsDataLog(date, date, employeeCode),
    enabled: !!employeeCode && !!date
  });

  if (isFetching) return <Skeleton.Button active size='small' style={{ width: 80, height: 20 }} />;
  if (!data?.data || data.data.length === 0) return <Text type='secondary'>Không có dữ liệu</Text>;

  const sortedLogs = [...data.data].sort((a, b) => new Date(a.record).getTime() - new Date(b.record).getTime());
  const first = sortedLogs[0];
  const last = sortedLogs[sortedLogs.length - 1];

  return (
    <Text strong>
      {formatTime(new Date(first.record))} - {sortedLogs.length > 1 ? formatTime(new Date(last.record)) : '...'}
    </Text>
  );
};

export default function AdditionalWorkingTimeTicketDetail() {
  const { setInvalidKey } = useContext(TicketContext);
  const { setAdditionalWorkingTimeTicket, setLoading } = useContext(HrTicketDetailContext);
  const { ticketSelected } = useContext(AppContext);
  const { id } = useParams();

  const breakpoints = useBreakpoint();
  const colspan = breakpoints.xxl ? 2 : 1;

  const additionalWorkingTimeTicketQuery = useQuery({
    queryKey: ['additionalWorkingTimeTicket', id],
    queryFn: () => getAdditionalWorkingTimeTicket(Number(id)),
    enabled: !!id && !!ticketSelected
  });

  useEffect(() => {
    if (additionalWorkingTimeTicketQuery.data) {
      setAdditionalWorkingTimeTicket(additionalWorkingTimeTicketQuery.data);
      setLoading(additionalWorkingTimeTicketQuery.isFetching);
      setInvalidKey(JSON.stringify(additionalWorkingTimeTicketQuery.data?.data));
    }
  }, [
    additionalWorkingTimeTicketQuery.data,
    additionalWorkingTimeTicketQuery.isFetching,
    setAdditionalWorkingTimeTicket,
    setInvalidKey,
    setLoading
  ]);

  const additionalWorkingTimeTicketDetailItems = (
    ticketStatusId: number | undefined,
    additionalWorkingTimeTicketDetails: IAdditionalWorkingTimeTicketDetail[]
  ): CollapseProps['items'] => {
    return additionalWorkingTimeTicketDetails.map((additionalWorkingTimeTicketDetail) => ({
      key: additionalWorkingTimeTicketDetail.id,
      label: (
        <Descriptions
          layout='horizontal'
          labelStyle={{ width: '135px' }}
          column={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1, xxl: 2 }}
        >
          <Descriptions.Item label='Người đăng ký'>
            <Text strong>{additionalWorkingTimeTicketDetail?.employee?.fullName}</Text>
          </Descriptions.Item>
          <Descriptions.Item label='Chi nhánh'>
            <Text>{additionalWorkingTimeTicketDetail?.branch?.name}</Text>
          </Descriptions.Item>

          {/* <Descriptions.Item label='Trạng thái'>
            {(() => {
              const isRejected = additionalWorkingTimeTicketDetail?.isRejected;
              if (ticketStatusId === TicketStatus.New) {
                return isRejected ? 'Đã loại bỏ' : 'Chờ phê duyệt';
              }
              if (ticketStatusId === TicketStatus.ApprovedRequest) {
                return isRejected ? 'Đã từ chối' : 'Đã duyệt';
              }
              if (ticketStatusId === TicketStatus.Canceled) {
                return 'Đã hủy';
              }
            })()}
          </Descriptions.Item> */}
          <Descriptions.Item label='Ngày đăng ký'>
            <Text>{formatDate(additionalWorkingTimeTicketDetail?.date)}</Text>
          </Descriptions.Item>
          <Descriptions.Item label='Loại bổ sung'>
            {additionalWorkingTimeTicketDetail?.additionalType?.name}
          </Descriptions.Item>
          <Descriptions.Item label='Giờ vào/ra'>
            {`${formatTimeString(String(additionalWorkingTimeTicketDetail?.startTime), true)} - ${formatTimeString(
              String(additionalWorkingTimeTicketDetail?.endTime)
            )}`}
          </Descriptions.Item>
          <Descriptions.Item label='Giờ chấm công'>
            <BiometricLogs
              employeeCode={additionalWorkingTimeTicketDetail?.employee?.code}
              date={additionalWorkingTimeTicketDetail?.date}
            />
          </Descriptions.Item>

          <Descriptions.Item label='Ghi chú'>{additionalWorkingTimeTicketDetail?.note}</Descriptions.Item>
        </Descriptions>
      ),
      showArrow: false,
      className: 'transportation-ticket-item'
    }));
  };

  return (
    <div className='additional-working-time-ticket-detail-wrapper'>
      <BtnHrHandle type='additionalWorkingTimeTicket' />

      <Divider style={{ marginTop: 16, marginBottom: 16 }} />

      <Fragment>
        <Flex justify='start'>
          <Title level={5}>Thông tin phiếu</Title>
        </Flex>
        {additionalWorkingTimeTicketQuery.isFetching ? (
          <Skeleton />
        ) : (
          <Fragment>
            <Descriptions
              layout='horizontal'
              labelStyle={{ width: '135px' }}
              column={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1, xxl: 2 }}
            >
              <Descriptions.Item label='Mã phiếu'>
                <Text strong>{additionalWorkingTimeTicketQuery.data?.data?.code}</Text>
              </Descriptions.Item>

              <Descriptions.Item label='Trạng thái'>
                <HRTicketStatusTag ticketStatus={additionalWorkingTimeTicketQuery.data?.data.ticketStatus} />
              </Descriptions.Item>
              <Descriptions.Item label='Ngày tạo' span={colspan}>
                <Text>{formatDate(additionalWorkingTimeTicketQuery.data?.data?.createdDate)}</Text>
              </Descriptions.Item>
              <Descriptions.Item label='Người tạo'>
                <Text>{additionalWorkingTimeTicketQuery.data?.data?.createdBy?.fullName}</Text>
              </Descriptions.Item>
              <Descriptions.Item label='Bộ phận'>
                <Text>
                  {
                    additionalWorkingTimeTicketQuery.data?.data?.additionalWorkingTimeTicketDetails?.[0]?.employee
                      ?.department?.name
                  }
                </Text>
              </Descriptions.Item>
              {/* <Descriptions.Item label='Package phê duyệt' span={colspan}>
                <Flex gap={6} align='center'>
                  <Text strong>{additionalWorkingTimeTicketQuery.data?.data?.additionalWorkingTimePackage?.code}</Text>
                  {additionalWorkingTimeTicketQuery.data?.data?.additionalWorkingTimePackage?.note && (
                    <Text type='secondary'>
                      ({additionalWorkingTimeTicketQuery.data?.data?.additionalWorkingTimePackage?.note})
                    </Text>
                  )}
                </Flex>
              </Descriptions.Item> */}
              <Descriptions.Item label='Ghi chú' span={colspan}>
                <Text strong>{additionalWorkingTimeTicketQuery.data?.data?.note}</Text>
              </Descriptions.Item>
            </Descriptions>
            <Divider style={{ marginTop: 16, marginBottom: 16 }} />
            <CollapseCustom
              title='Danh sách item'
              titleLevel={5}
              items={additionalWorkingTimeTicketDetailItems(
                additionalWorkingTimeTicketQuery.data?.data.ticketStatus.id || undefined,
                additionalWorkingTimeTicketQuery.data?.data.additionalWorkingTimeTicketDetails || []
              )}
              expandIconPosition='end'
              className='additional-working-time-ticket-list'
            />
          </Fragment>
        )}
      </Fragment>
    </div>
  );
}
